import React, { FC } from 'react';
import { LoadingButton } from '@mui/lab';
import { Chip } from '@mui/material';
import pick from 'lodash/pick';
import { useSelector } from 'react-redux';

import { ICreateAbsenceRequestDTO, IEmployeeBalanceDTO, IScheduleStatusDTO } from 'src/api/dtos';
import { FutureBalanceCheck } from 'src/features/absences/future-balance';
import { UserXIcon } from 'src/shared/components';
import { FeaturesEnums, PermissionsEnums } from 'src/shared/enums';
import { ComponentWithFeatures } from 'src/shared/hocs';
import { usePermissions } from 'src/shared/hooks';
import { useTranslate } from 'src/shared/providers';
import { isAbsenceRequestInvalid, isBalanceCheckInvalid } from 'src/shared/utils';
import { getAbsenceTypesByPartialCheck } from 'src/store/absence/absences.selectors';
import { RootState } from 'src/store/types';

import {
  AbsenceNotesInput,
  AbsenceTypeSelect,
  FullDaysAbsenceInputs,
  PartialAbsenceCheckbox,
  PartialAbsenceInputs,
} from './form-inputs';
import './AbsenceRequestForm.styles.scss';

interface Props {
  inputs: Partial<ICreateAbsenceRequestDTO>;
  inputTargetKey: keyof ICreateAbsenceRequestDTO | undefined;
  hasErrors: boolean;
  scheduleWarnings: IScheduleStatusDTO[];
  balance: IEmployeeBalanceDTO | undefined;
  loadingBalance: boolean;
  onSubmit: () => void;
  onNavigate: () => void;
  onBalanceCheck: () => void;
  onCheck: () => void;
  onUpdate: (inputs: Partial<ICreateAbsenceRequestDTO>) => void;
  onBalanceCancel: () => void;
}

export const AbsenceRequestForm: FC<Props> = ({
  inputs,
  inputTargetKey,
  hasErrors,
  scheduleWarnings,
  balance,
  loadingBalance,
  onSubmit,
  onNavigate,
  onBalanceCheck,
  onBalanceCancel,
  onCheck,
  onUpdate,
}) => {
  const { t } = useTranslate();

  const { settings, preferences } = useSelector((state: RootState) => state.user);
  const { approvers, submitting } = useSelector((state: RootState) => state.absences);
  const absenceTypes = useSelector(getAbsenceTypesByPartialCheck(!!inputs.isPartialDay));

  const hasPartialAbsencesPermission = usePermissions(PermissionsEnums.PARTIAL_ABSENCES_EDIT);
  const isPartialDayEnabled = settings?.isPartialAbsenceEnabled && hasPartialAbsencesPermission;

  const disabled = isAbsenceRequestInvalid(inputs) || hasErrors || isBalanceCheckInvalid(balance, settings);

  return (
    <div className="d-flex flex-column absence-requests">
      {isPartialDayEnabled && <PartialAbsenceCheckbox isPartialDay={!!inputs.isPartialDay} onCheck={onCheck} />}
      {inputs.isPartialDay ? (
        <PartialAbsenceInputs
          inputs={pick(inputs, 'startDate', 'endDate', 'endTime', 'startTime')}
          inputTargetKey={inputTargetKey}
          scheduledWarnings={scheduleWarnings}
          preferences={preferences}
          onUpdate={onUpdate}
        />
      ) : (
        <FullDaysAbsenceInputs inputs={pick(inputs, 'startDate', 'endDate')} onUpdate={onUpdate} />
      )}
      <div className="my-3">
        <AbsenceTypeSelect absenceTypeId={inputs.absenceTypeId} absenceTypes={absenceTypes} onUpdate={onUpdate} />
      </div>
      <ComponentWithFeatures features={FeaturesEnums.AccrualsFunction}>
        <FutureBalanceCheck
          balance={balance}
          loading={loadingBalance}
          disabled={disabled}
          settings={settings}
          onOpen={onNavigate}
          onValidate={onBalanceCheck}
          onCancelRequest={onBalanceCancel}
        />
      </ComponentWithFeatures>
      <div className="my-3">
        <AbsenceNotesInput note={inputs.notes || ''} onChange={(value) => onUpdate({ notes: value })} />
      </div>
      <div className="mb-4">
        <div className="d-flex align-items-center">
          <label className="me-2">{t('absences.approverThatWillBeNotified')}</label>
          {approvers.map((approver, index) => (
            <Chip className="me-2" key={`${approver}-${index}`} label={approver} />
          ))}
        </div>
        <div className="my-4">
          <div className="d-flex">
            <LoadingButton
              color="primary"
              variant="contained"
              loadingPosition="start"
              loading={submitting}
              disabled={disabled}
              startIcon={<UserXIcon />}
              onClick={() => onSubmit()}
            >
              {t('absences.submitAbsenceRequest')}
            </LoadingButton>
          </div>
        </div>
      </div>
    </div>
  );
};
